import {inject} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot} from '@angular/router';
import {Store} from '@ngrx/store';
import {map, take} from 'rxjs/operators';
import {AppState} from '../store/states/app.state';
import {UserViewConfigSelectors} from '../store/selector/user-view-config.selectors';


/**
 * Dieser Guard ist nur auf Unterseiten gesetzt, damit ein Benutzer
 * niemals auf einer Unterseite landet, bevor das Frontend den
 * userViewConfig-State vom Backend geladen hat.
 *
 * Der State ist notwendig, um zu bestimmen, auf welcher Seite
 * die App tatsächlich startet.
 *
 * Ablauf:
 * 1. Benutzer ruft eine Unterseite auf.
 * 2. Der Guard wird ausgelöst.
 * 3. Prüfen, ob userViewConfig im Store gültig ist:
 *    - Ja → Prüfen, ob Zugriff auf die gewünschte Seite erlaubt ist:
 *        - Erlaubt → Benutzer gelangt auf die gewünschte Seite.
 *        - Nicht erlaubt → Benutzer wird zur Init-Seite navigiert.
 *    - Nein → Benutzer wird zur Init-Seite navigiert, den Backend-Fetch
 *             auslöst, der Store aktualisiert und anschließend
 *             automatisch zur Hauptseite navigiert.
 */

export const userViewConfigGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const store = inject(Store<AppState>);
  const router = inject(Router);

  return store.select(UserViewConfigSelectors.userViewConfigState).pipe(
    take(1),
    map(userViewConfig => {
      const urlPath = state.url.split('/')[1]?.split('?')[0];

      // INFO: user-view-config sollte immer erlaubt sein, auch wenn keine Config vorhanden ist
      if (urlPath === 'user-view-config') {
        return true;
      }

      if (!userViewConfig.meta.isValidConfigAvailable) {
        return router.createUrlTree(['/init-page']);
      }

      const config = userViewConfig;
      const hasNews = config?.news?.show === true;
      const hasWeather = config?.weather?.show === true;
      const hasCharts = config?.agrarcharts?.show === true;

      switch (urlPath) {
        case 'overview':
          if (hasNews && (hasWeather || hasCharts)) {
            return true;
          }
          return router.createUrlTree(['/init-page']);

        case 'news':
          if (hasNews) {
            return true;
          }
          return router.createUrlTree(['/init-page']);

        case 'weather':
          if (hasWeather) {
            return true;
          }
          return router.createUrlTree(['/init-page']);

        case 'agrarcharts':
          if (hasCharts) {
            return true;
          }
          return router.createUrlTree(['/init-page']);

        default:
          return router.createUrlTree(['/init-page']);
      }
    })
  );
};
